"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import type { i18n as I18nInstance } from "i18next";
import Container from "@/components/common/Container";
import { MarketingCtaSection } from "@/components/redesign/MarketingCtaSection";
import NotFoundHero from "@/components/not-found/NotFoundHero";
import NotFoundBlogContent from "@/components/not-found/NotFoundBlogContent";
import type { BlogPost } from "@/utils/blog";
import { isBlogPath } from "@/utils/notFound";
import { resolveLangFromPathname } from "@/utils/library/languages";

function subscribeToHydration() {
  return () => {};
}

export default function NotFoundContent() {
  const requestPath = usePathname() ?? "/";
  // not-found renders "/" on the server. The real path is applied after hydration.
  const isClient = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false,
  );
  const pathname = isClient ? requestPath : "/";

  const lang = resolveLangFromPathname(pathname);
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(lang);
  const blogPath = isBlogPath(pathname);

  return (
    <Container headerVariant="redesign">
      <NotFoundHero
        title={t("notFound.title")}
        description={t("notFound.description")}
        backHomeLabel={t("notFound.backHome")}
        lang={lang}
      />
      {blogPath ? (
        <NotFoundBlogPosts lang={lang} i18n={i18n} />
      ) : null}
      <MarketingCtaSection
        title={t("redesign.cta.title")}
        subtitle={t("redesign.cta.subtitle")}
        emailPlaceholder={t("redesign.cta.emailPlaceholder")}
        ctaLabel={t("redesign.cta.ctaLabel")}
        personalEmailWarning={t("hero.personalEmailWarning")}
        continueAnywayLabel={t("hero.continueAnyway")}
        getStartedPrompt={t("redesign.cta.getStartedPrompt")}
        getStartedLinkLabel={t("redesign.cta.getStartedLinkLabel")}
        source="not_found_cta"
      />
    </Container>
  );
}

function NotFoundBlogPosts({
  lang,
  i18n,
}: Readonly<{ lang: string; i18n: I18nInstance }>) {
  const [posts, setPosts] = useState<BlogPost[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`/api/not-found-blog/${lang}`)
      .then(async (response) => {
        if (!response.ok) return [];
        const body = (await response.json()) as { posts?: BlogPost[] };
        return body.posts ?? [];
      })
      .then((nextPosts) => {
        if (!cancelled) setPosts(nextPosts);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  if (posts == null) return null;

  return <NotFoundBlogContent posts={posts} lang={lang} i18n={i18n} />;
}
